import { useCallback, useEffect, useMemo, useState } from 'react'
import { can, type OffboardingGate } from '../lib/api'
import { PageTabs, type PageTab } from './kit'
import { exitApi, type ExitSummary, type ExitView, type ExitItemState, type LegalHold } from '../lib/exit'
import { ErrorBox, Loading } from './DataTable'
import { loadVendorSummary } from './exit/board'
import { ExitChainPanel, SignDrawer, type Signing } from './exit/ExitChainPanel'
import { ExitEvidencePanel } from './exit/ExitEvidencePanel'
import { ExitHeader } from './exit/ExitHeader'
import { ExitMessagesPanel } from './exit/ExitMessagesPanel'
import { ExitPlanPanel } from './exit/ExitPlanPanel'
import { ExitStagePanel } from './exit/ExitStagePanel'
import { LegalHoldsPanel } from './exit/LegalHoldsPanel'
import { useSubject } from './exit/hooks'
import '../styles/exit.css'

// The exit of one vendor: where it is, where it is going, what happens next
// and who acts.
//
// # WHAT THIS REPLACES
//
// Six stacked paragraphs: the chain as prose, each stage's full reason inline,
// the checklist below as eleven cards of text, legal holds and the plan under
// that. The founder read it as unprofessional, and he was right that nobody
// could see at a glance whether the exit was moving. Here the chain is a track
// with the next person in one bar, the evidence is a ring and a task list, the
// plan is a line against the calendar, and every reason is one press away in
// a tip or a drawer rather than gone.
//
// # WHERE IT IS DRAWN
//
// On its own page, #/exits/<vendorId>, full width: the head and the stage
// panel across the top, and the parts under it as tabs (3.3, O2): Sign-offs,
// Evidence, Exit plan, Legal holds, Messages. It was a section of the vendor
// record once, squeezed beside the section menu, and read as basic there.
//
// # WHY TABS (12 Oct 2026)
//
// Under the stage panel the five parts stood in two columns, and the page was
// two windows tall at 1280 x 720 and nearly three at 1024 x 768: the evidence
// list alone is eleven rows, and the holds and messages sat under it, out of
// sight. One part at a time keeps the page near one window, and every tab
// carries what is waiting in it (stages still to sign, evidence still to
// record, standing holds), so nothing is hidden by the fold. The open tab is in
// the address (?tab=evidence) and replaces the entry rather than adding one, so
// Back leaves the exit instead of walking through its tabs (the Back model).
// "Record" on a chain stage's evidence opens the Evidence tab on that item.
//
// # WHERE THE ANSWERS COME FROM
//
// Every stage, role name, refusal and checklist sentence arrives from
// GET /v1/vendors/{id}/exit, as before. The stage an exit has reached, what is
// holding it, which stage owns which evidence and the plan's end date come
// from GET /v1/exits for this vendor; where that read is not available the
// workspace shows none of those rather than guessing them.

export type ExitTab = 'signoffs' | 'evidence' | 'plan' | 'holds' | 'messages'
const EXIT_TABS: ExitTab[] = ['signoffs', 'evidence', 'plan', 'holds', 'messages']

// The tab the address names, if it names one.
export function exitTabInAddress(hash: string): ExitTab | null {
  const t = new URLSearchParams(hash.split('?')[1] ?? '').get('tab')
  return EXIT_TABS.includes(t as ExitTab) ? (t as ExitTab) : null
}

// The address with the tab in it: Sign-offs, the first, is the address without one.
export function exitTabAddress(hash: string, tab: ExitTab): string {
  const [path, query] = hash.replace(/^#/, '').split('?')
  const q = new URLSearchParams(query ?? '')
  if (tab === 'signoffs') q.delete('tab')
  else q.set('tab', tab)
  const rest = q.toString()
  return '#' + path + (rest ? '?' + rest : '')
}

// What each tab says it holds, from what the workspace has read: the stages
// still to sign, the REQUIRED evidence still to record (the optional items hold
// nothing, so they are not counted), the standing holds. A tab that is not
// readable by this person is left out.
export function exitTabs({
  chain,
  gate,
  holds,
  holdsFailed = false,
  readsRegister,
  signable,
  blocked,
  underWay = true,
}: {
  chain: { unsigned: unknown[] }
  gate?: { blocking: unknown[] }
  holds: { standing: boolean }[] | null
  // The holds could not be read: the tab must not look like "none".
  holdsFailed?: boolean
  readsRegister: boolean
  signable: boolean
  blocked: boolean
  // Evidence is recorded only while the exit is under way; before that a count
  // would read as work nobody can do yet.
  underWay?: boolean
}): PageTab<ExitTab>[] {
  const tabs: PageTab<ExitTab>[] = [
    { key: 'signoffs', label: 'Sign-offs', count: chain.unsigned.length, ...(signable ? { alert: 'yours to sign' } : {}) },
  ]
  if (gate)
    tabs.push({
      key: 'evidence',
      label: 'Evidence',
      ...(underWay ? { count: gate.blocking.length } : {}),
      ...(blocked ? { alert: 'holding offboarding' } : {}),
    })
  if (readsRegister) {
    tabs.push({ key: 'plan', label: 'Exit plan' })
    const standing = (holds ?? []).filter((h) => h.standing).length
    tabs.push({
      key: 'holds',
      label: 'Legal holds',
      ...(standing > 0 ? { count: standing } : {}),
      ...(holdsFailed ? { alert: 'could not be read' } : {}),
    })
    tabs.push({ key: 'messages', label: 'Messages' })
  }
  return tabs
}

export function VendorExit({
  vendorId,
  head,
  onChanged,
}: {
  vendorId: string
  // The vendor's name and code, for the page's title.
  head: { name: string; code: string }
  onChanged?: () => void
}) {
  const [view, setView] = useState<ExitView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [summary, setSummary] = useState<ExitSummary | null>(null)
  const [holds, setHolds] = useState<LegalHold[] | null>(null)
  const [holdsError, setHoldsError] = useState<unknown>(null)
  const [signing, setSigning] = useState<Signing | null>(null)
  const [openItem, setOpenItem] = useState<string | null>(null)
  const [announce, setAnnounce] = useState('')
  const [tab, setTabState] = useState<ExitTab>(() => exitTabInAddress(window.location.hash) ?? 'signoffs')
  const setTab = useCallback((next: ExitTab) => {
    setTabState(next)
    history.replaceState(history.state, '', exitTabAddress(window.location.hash, next))
  }, [])
  const subject = useSubject()
  const readsRegister = can('vendor.read')

  const load = useCallback(() => {
    exitApi
      .vendorExit(vendorId)
      .then((v) => {
        setView(v)
        setError(null)
      })
      .catch(setError)
    if (readsRegister) void loadVendorSummary(vendorId).then(setSummary)
  }, [vendorId, readsRegister])

  const loadHolds = useCallback(() => {
    if (!readsRegister) return
    exitApi
      .legalHolds(vendorId)
      .then((r) => {
        setHolds(r.holds)
        setHoldsError(null)
      })
      .catch(setHoldsError)
  }, [vendorId, readsRegister])

  useEffect(load, [load])
  useEffect(loadHolds, [loadHolds])

  const changed = useCallback(() => {
    load()
    loadHolds()
    onChanged?.()
  }, [load, loadHolds, onChanged])

  // Which stage of the chain vouches for each evidence item, by job name.
  const owners = useMemo(() => {
    const out: Record<string, string> = {}
    for (const st of summary?.stages ?? []) for (const k of st.items) out[k] = st.job
    return out
  }, [summary])

  if (error != null) return <ErrorBox error={error} what="The offboarding" />
  if (view == null) return <Loading what="the offboarding" />

  const chain = view.exitApproval
  const gate: OffboardingGate | undefined = view.offboarding
  const underWay = view.status === 'OFFBOARDING'
  const recordable = underWay
  const mayRecord = recordable && can('offboarding.run')
  const exit = chain.exit

  // The directory's proof that the vendor's accounts are closed, placed on the
  // stage that vouches for the item: the one GET /v1/exits names, else the CISO.
  const credentials = (gate?.items ?? []).find((i) => i.key === 'CREDENTIALS_DISABLED') as ExitItemState | undefined
  const observed = credentials?.observed
  const accountsStage = summary?.stages.find((st) => st.items.includes('CREDENTIALS_DISABLED'))?.key ?? 'CISO'
  const accounts = observed
    ? { stageKey: accountsStage, closed: observed.closed, total: observed.total, met: observed.met, itemKey: 'CREDENTIALS_DISABLED' }
    : undefined

  const tabs = exitTabs({
    chain,
    gate,
    holds,
    holdsFailed: holdsError != null,
    readsRegister,
    signable: view.signable,
    blocked: underWay && Boolean(summary?.blockerItemKey),
    underWay,
  })
  // A tab this person cannot open (or one the address names that is not here)
  // falls back to the first.
  const open: ExitTab = tabs.some((t) => t.key === tab) ? tab : 'signoffs'

  return (
    <div className="exit-workspace">
      <ExitHeader
        vendorId={vendorId}
        vendorName={head.name}
        vendorCode={head.code}
        status={view.status}
        chain={chain}
        summary={summary}
        onChanged={changed}
      />

      <ExitStagePanel
        vendorId={vendorId}
        status={view.status}
        chain={chain}
        summary={summary}
        signable={view.signable}
        subject={subject}
        announce={announce}
        onSign={setSigning}
        onChanged={changed}
      />

      <PageTabs label="Offboarding" tabs={tabs} value={open} onChange={setTab}>
        {open === 'signoffs' && (
          <ExitChainPanel
            chain={chain}
            underWay={underWay}
            signable={view.signable}
            subject={subject}
            blocker={summary?.blocker}
            blockerItemKey={summary?.blockerItemKey}
            dueOn={summary?.dueOn}
            mayRecord={mayRecord}
            accounts={readsRegister ? accounts : undefined}
            onSign={setSigning}
            onRecordItem={(key) => {
              // The button pressed goes with the Sign-offs tab, so focus moves
              // to the Evidence tab first: the drawer that opens returns there
              // when it closes, not to the top of the page.
              if (gate) {
                document.querySelector<HTMLElement>('[role="tab"][data-tab="evidence"]')?.focus()
                setTab('evidence')
              }
              setOpenItem(key)
            }}
          />
        )}
        {open === 'evidence' && gate && (
          <ExitEvidencePanel
            vendorId={vendorId}
            gate={gate}
            recordable={recordable}
            owners={owners}
            holds={holds}
            openKey={openItem}
            onOpen={setOpenItem}
            startedOn={exit && exit.status !== 'PROPOSED' && exit.openedAt ? exit.openedAt.slice(0, 10) : undefined}
            onRecorded={(next) => {
              setView((prev) => (prev ? { ...prev, offboarding: next } : prev))
              changed()
            }}
          />
        )}
        {open === 'plan' && readsRegister && (
          <ExitPlanPanel
            vendorId={vendorId}
            dates={{
              openedAt: exit && exit.status !== 'PROPOSED' ? exit.openedAt : undefined,
              closedAt: view.status === 'OFFBOARDED' ? exit?.closedAt : undefined,
              dueOn: summary?.dueOn,
              items: (gate?.items ?? []) as ExitItemState[],
            }}
            onSaved={changed}
          />
        )}
        {open === 'holds' && readsRegister && (
          <LegalHoldsPanel vendorId={vendorId} holds={holds} error={holdsError} onChanged={changed} />
        )}
        {open === 'messages' && readsRegister && <ExitMessagesPanel vendorId={vendorId} />}
      </PageTabs>

      <SignDrawer
        vendorId={vendorId}
        signing={signing}
        onClose={() => setSigning(null)}
        onDecided={(r) => {
          setSigning(null)
          const next = r.exitApproval.stages.find((s) => s.next)
          setAnnounce(
            (r.decision.decision === 'DECLINED' ? 'Declined the ' : 'Signed the ') +
              r.decision.job +
              ' stage. ' +
              (next ? 'Next: ' + next.job + '.' : 'Every stage is signed.'),
          )
          setView((prev) => (prev ? { ...prev, exitApproval: r.exitApproval } : prev))
          if (readsRegister) void loadVendorSummary(vendorId).then(setSummary)
          onChanged?.()
        }}
      />
    </div>
  )
}
